import React, { useEffect, useState, useRef } from 'react';
import { FilterIcon, XIcon, ChevronDownIcon, CheckIcon, SearchIcon } from 'lucide-react';
import { DIVISION_LEADS, TEAM_LEADS, ALERT_TYPES, AlertType } from '../data/mockData';
interface PipelineFiltersProps {
  selectedDivision: string;
  setSelectedDivision: (val: string) => void;
  selectedTeam: string;
  setSelectedTeam: (val: string) => void;
  selectedAlerts: AlertType[];
  setSelectedAlerts: (val: AlertType[]) => void;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  onClearFilters: () => void;
  totalLoans: number;
}
export const PipelineFilters: React.FC<PipelineFiltersProps> = ({
  selectedDivision,
  setSelectedDivision,
  selectedTeam,
  setSelectedTeam,
  selectedAlerts,
  setSelectedAlerts,
  searchQuery,
  setSearchQuery,
  onClearFilters,
  totalLoans
}) => {
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const alertsRef = useRef<HTMLDivElement>(null);
  // Close alerts dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (alertsRef.current && !alertsRef.current.contains(event.target as Node)) {
        setIsAlertsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const availableTeams = selectedDivision ? TEAM_LEADS[selectedDivision] || [] : Object.values(TEAM_LEADS).flat();
  const toggleAlert = (alert: AlertType) => {
    if (selectedAlerts.includes(alert)) {
      setSelectedAlerts(selectedAlerts.filter(a => a !== alert));
    } else {
      setSelectedAlerts([...selectedAlerts, alert]);
    }
  };
  const hasActiveFilters = selectedDivision !== '' || selectedTeam !== '' || selectedAlerts.length > 0 || searchQuery !== '';
  return <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex flex-col gap-3 transition-colors duration-200" data-id="element-62">
      {/* Top Row: Filters + Loan Count */}
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-63">
        <div className="flex flex-wrap items-center gap-4" data-id="element-64">
          {/* Search Input */}
          <div className="relative" data-id="element-65">
            <SearchIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-66" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search borrower, loan #, officer..." className="text-sm border border-gray-300 dark:border-gray-600 rounded-md pl-9 pr-3 py-2 bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:border-transparent w-64 placeholder-gray-400 dark:placeholder-gray-500 transition-colors duration-200" data-id="element-67" />
          </div>

          <div className="h-6 w-px bg-gray-200 dark:bg-gray-600 hidden sm:block transition-colors duration-200" data-id="element-68"></div>

          <div className="flex items-center text-gray-500 dark:text-gray-400 mr-2 transition-colors duration-200" data-id="element-69">
            <FilterIcon className="w-4 h-4 mr-2" data-id="element-70" />
            <span className="text-sm font-medium uppercase tracking-wider" data-id="element-71">
              Filters
            </span>
          </div>

          {/* Division Lead Filter */}
          <select value={selectedDivision} onChange={e => {
          setSelectedDivision(e.target.value);
          setSelectedTeam(''); // Reset team when division changes
        }} className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:border-transparent min-w-[160px] transition-colors duration-200" data-id="element-72">
            <option value="" data-id="element-73">All Division Leads</option>
            {DIVISION_LEADS.map(lead => <option key={lead} value={lead} data-id="element-74">
                {lead}
              </option>)}
          </select>

          {/* Team Lead Filter */}
          <select value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)} className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:border-transparent min-w-[160px] transition-colors duration-200" data-id="element-75">
            <option value="" data-id="element-76">All Team Leads</option>
            {availableTeams.map(lead => <option key={lead} value={lead} data-id="element-77">
                {lead}
              </option>)}
          </select>

          {/* Alerts Multi-Select */}
          <div className="relative" ref={alertsRef} data-id="element-78">
            <button onClick={() => setIsAlertsOpen(!isAlertsOpen)} className={`text-sm border rounded-md px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-[#6DB33F] focus:border-transparent flex items-center justify-between min-w-[180px] transition-colors duration-200 ${selectedAlerts.length > 0 ? 'border-[#6DB33F] bg-[#6DB33F]/5 text-[#6DB33F] font-medium' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200'}`} data-id="element-79">
              <span className="truncate pr-2" data-id="element-80">
                {selectedAlerts.length === 0 ? 'All Alerts' : `${selectedAlerts.length} Alert${selectedAlerts.length > 1 ? 's' : ''} Selected`}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-gray-400" data-id="element-81" />
            </button>

            {isAlertsOpen && <div className="absolute top-full left-0 mt-1 w-72 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50 py-1 transition-colors duration-200" data-id="element-82">
                {ALERT_TYPES.map(alert => {
              const isSelected = selectedAlerts.includes(alert);
              return <button key={alert} onClick={() => toggleAlert(alert)} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 flex items-center gap-2 transition-colors duration-200" data-id="element-83">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors duration-200 ${isSelected ? 'bg-[#6DB33F] border-[#6DB33F]' : 'border-gray-300 dark:border-gray-500'}`} data-id="element-84">
                        {isSelected && <CheckIcon className="w-3 h-3 text-white" data-id="element-85" />}
                      </div>
                      <span className={isSelected ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'} data-id="element-86">
                        {alert}
                      </span>
                    </button>;
            })}
              </div>}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && <button onClick={onClearFilters} className="text-sm text-gray-500 dark:text-gray-400 hover:text-[#6DB33F] dark:hover:text-[#6DB33F] flex items-center gap-1 px-2 py-2 transition-colors" data-id="element-87">
              <XIcon className="w-4 h-4" data-id="element-88" />
              Clear
            </button>}
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-300 font-medium bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full whitespace-nowrap transition-colors duration-200" data-id="element-89">
          Showing {totalLoans} Loan{totalLoans !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Active Alert Chips - separate row, doesn't affect filter positions */}
      {selectedAlerts.length > 0 && <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-700 transition-colors duration-200" data-id="element-90">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-1 transition-colors duration-200" data-id="element-91">
            Active Alerts:
          </span>
          {selectedAlerts.map(alert => <span key={alert} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#6DB33F]/10 text-[#6DB33F] border border-[#6DB33F]/20" data-id="element-92">
              {alert}
              <button onClick={() => toggleAlert(alert)} className="ml-0.5 hover:text-green-800 dark:hover:text-green-400 transition-colors" aria-label={`Remove ${alert} filter`} data-id="element-93">
                <XIcon className="w-3 h-3" data-id="element-94" />
              </button>
            </span>)}
          <button onClick={() => setSelectedAlerts([])} className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-1 transition-colors" data-id="element-95">
            Clear alerts
          </button>
        </div>}
    </div>;
};